import { getFeedbackQuestionnaireData, type Question } from '@aikoa/common'
import { type TFunction } from '@aikoa/i18n/react'
import z from 'zod/v4'
import { type FlowPhase } from '~/components/flows/FlowProvider'
import { type FeedbackFlowData } from './feedbackFlowTypes'

export function buildQuestionnairePhases(
  t: TFunction<'feedback'>
): FlowPhase<string>[] {
  const questionnaireData = getFeedbackQuestionnaireData(t)
  const questionnaireQuestions = questionnaireData.questions

  const phases: FlowPhase<string>[] = []
  const insertedQuestionIds = new Set<string>()

  const baseQuestions = questionnaireQuestions.filter(
    (question) => !question.conditions || question.conditions.length === 0
  )

  const conditionalQuestions = questionnaireQuestions.filter(
    (question) => question.conditions && question.conditions.length > 0
  )

  const childrenByParent = new Map<string, (typeof questionnaireQuestions)[0][]>()

  for (const question of conditionalQuestions) {
    for (const condition of question.conditions ?? []) {
      const list = childrenByParent.get(condition.id) ?? []
      list.push(question)
      childrenByParent.set(condition.id, list)
    }
  }

  function questionIsActive(
    question: Question,
    answers: Partial<FeedbackFlowData['feedbackAnswers']>
  ): boolean {
    const conditions = question.conditions ?? []

    if (conditions.length === 0) {
      return true
    }

    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i]

      const conditionQuestion = questionnaireQuestions.find(
        (questionEntry) => questionEntry.id === condition.id
      )

      if (!conditionQuestion) {
        continue
      }

      if (conditionQuestion.conditions && !questionIsActive(conditionQuestion, answers)) {
        continue
      }

      const value = answers[condition.id as keyof typeof answers]

      if (typeof value === 'number' && value === condition.value) {
        return true
      }
    }

    return false
  }

  function createPhaseForQuestion(
    question: (typeof questionnaireQuestions)[0],
    isConditional: boolean
  ) {
    const answerSchema = question.type === 'text-input' ? z.string() : z.number()

    return {
      id: question.id,
      title: question.title ?? questionnaireData.name,
      dataKey: 'feedbackAnswers' as const,
      schema: z
        .looseObject({
          [question.id]: answerSchema,
        })
        .refine((data) => {
          const value = data?.[question.id]
          if (question.type === 'text-input') {
            return typeof value === 'string' && value.trim().length > 0
          }
          return value !== undefined
        }),
      usesCustomButtons: (data: Partial<FeedbackFlowData>) =>
        data?.feedbackAnswers?.[question.id] === undefined,
      hiddenFromProgress: isConditional,
      condition: isConditional
        ? (data: Partial<FeedbackFlowData>) => {
            const answers = (data as Partial<FeedbackFlowData>)?.feedbackAnswers ?? {}
            return questionIsActive(question, answers)
          }
        : undefined,
    }
  }

  function insertChildrenAfter(parentId: string) {
    const children = childrenByParent.get(parentId) ?? []

    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (insertedQuestionIds.has(child.id)) {
        continue
      }

      const childPhase = createPhaseForQuestion(child, true)
      phases.push(childPhase)
      insertedQuestionIds.add(child.id)

      insertChildrenAfter(child.id)
    }
  }

  for (const baseQuestion of baseQuestions) {
    const basePhase = createPhaseForQuestion(baseQuestion, false)
    phases.push(basePhase)
    insertedQuestionIds.add(baseQuestion.id)
    insertChildrenAfter(baseQuestion.id)
  }

  return phases
}
